import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Search, Star, Check, X, Eye } from "lucide-react";

export default function CandidatesList({ user }) {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [stages, setStages] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStage, setSelectedStage] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [candResp, rolesResp, stagesResp] = await Promise.all([
          api.get("/candidates"),
          api.get("/roles"),
          api.get("/stages"),
        ]);
        setCandidates(candResp.data);
        setRoles(rolesResp.data);
        setStages(stagesResp.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = candidates.filter((c) => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.email || "").toLowerCase().includes(search.toLowerCase());
    const matchRole = !selectedRole || selectedRole === "all" || c.role_id === selectedRole;
    const matchStage = !selectedStage || selectedStage === "all" || c.greenhouse_stage === selectedStage;
    return matchSearch && matchRole && matchStage;
  });

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.role_id === roleId);
    return role?.title || "Unassigned";
  };

  return (
    <div className="px-4 py-6 sm:p-6 md:p-8 lg:p-12 space-y-4 lg:space-y-6 animate-fade-in" data-testid="candidates-list-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white uppercase">Candidates</h1>
        <p className="text-zinc-500 mt-1">All candidates in the pipeline</p>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="candidate-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search candidates..." className="pl-10 bg-zinc-800 border-white/10 text-zinc-200 placeholder:text-zinc-600" />
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedStage} onValueChange={setSelectedStage}>
            <SelectTrigger className="w-full sm:w-48 bg-zinc-800 border-white/10 text-zinc-300" data-testid="candidates-stage-filter">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10">
              <SelectItem value="all">All Stages</SelectItem>
              {stages.map((s) => <SelectItem key={s.name} value={s.name}>{s.name} ({s.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-full sm:w-48 bg-zinc-800 border-white/10 text-zinc-300" data-testid="candidates-role-filter">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-white/10">
              <SelectItem value="all">All Roles</SelectItem>
              {roles.map((r) => <SelectItem key={r.role_id} value={r.role_id}>{r.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="font-mono bg-zinc-800 border border-white/10 text-zinc-400 shrink-0">{filtered.length}</Badge>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No candidates found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="candidates-grid">
          {filtered.map((c) => {
            const parsed = c.parsed_data || {};
            return (
              <Card key={c.candidate_id} data-testid={`candidate-card-${c.candidate_id}`} className="bg-zinc-900/50 border-white/10 hover:border-white/20 cursor-pointer transition-colors group" onClick={() => navigate(`/candidates/${c.candidate_id}`)}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-semibold text-white group-hover:text-blue-400 transition-colors">{c.name}</h3>
                      <p className="text-xs text-zinc-500 mt-0.5">{parsed.current_role || c.greenhouse_role || "Candidate"}</p>
                    </div>
                    <Eye className="w-4 h-4 text-zinc-600 group-hover:text-blue-400 transition-colors" />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Badge variant="outline" className="text-xs text-zinc-400 border-white/10">{getRoleName(c.role_id)}</Badge>
                    <Badge variant="outline" className="text-xs text-zinc-500 border-white/10 font-mono">{c.source}</Badge>
                    {c.greenhouse_stage && (
                      <Badge variant="outline" className="text-xs text-blue-400/70 border-blue-500/20">{c.greenhouse_stage}</Badge>
                    )}
                  </div>
                  {parsed.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {parsed.skills.slice(0, 4).map((s, i) => <span key={i} className="px-2 py-0.5 text-xs rounded bg-zinc-800 text-zinc-400 border border-white/5">{s}</span>)}
                      {parsed.skills.length > 4 && <span className="px-2 py-0.5 text-xs rounded bg-zinc-800 text-zinc-500">+{parsed.skills.length - 4}</span>}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-xs"><Check className="w-3 h-3 text-emerald-400" /><span className="text-zinc-400">{c.accept_count || 0}</span></span>
                      <span className="flex items-center gap-1 text-xs"><X className="w-3 h-3 text-red-400" /><span className="text-zinc-400">{c.reject_count || 0}</span></span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-amber-400" />
                      <span className="font-mono text-xs text-zinc-300">{c.avg_score || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
